import { supabase } from '../supabase/client';
import { User } from '../../types';
import { BlacklistGuard } from './blacklistGuard';
import { ActivityService } from './activityService';
import { resolveProfileImageUrl } from '../../utils/profileImageUrl';

/** 將 users.profile_image / OAuth avatar 轉成可顯示 URL；無圖時回傳空字串（Header 顯示預設圖示） */
function mapUserProfileImage(
  raw: string | null | undefined,
  oauthFallback?: string | null
): string {
  const source =
    (raw && String(raw).trim()) || (oauthFallback && String(oauthFallback).trim()) || '';
  if (!source) return '';
  return resolveProfileImageUrl(source, { fallback: '' });
}

// 錯誤信息轉換函數
const translateErrorMessage = (message: string): string => {
  const errorMappings: { [key: string]: string } = {
    'Invalid login credentials': '帳號或密碼錯誤',
    'Email not confirmed': '電子郵件尚未確認，請檢查您的郵箱並點擊確認連結',
    'User not found': '找不到用戶，請檢查電子郵件地址是否正確',
    'Invalid email': '電子郵件格式無效',
    'Password should be at least 6 characters': '密碼至少需要6個字符',
    'User already registered': '此電子郵件已被註冊，請使用其他郵箱或嘗試登入',
    'Invalid email format': '電子郵件格式不正確',
    'Email address not valid': '電子郵件地址無效',
    'Password is too weak': '密碼強度太弱，請使用更強的密碼',
    'Too many requests': '請求過於頻繁，請稍後再試',
    'Network error': '網路連線錯誤，請檢查您的網路連線',
    'Unable to validate email address: invalid format': '無法驗證電子郵件地址：格式無效',
    'Signup requires a valid password': '註冊需要有效的密碼',
    'Invalid credentials': '憑證無效',
    'Email rate limit exceeded': '電子郵件發送頻率過高，請稍後再試',
    'Signup disabled': '註冊功能已停用',
    'Email address is already registered': '電子郵件地址已被註冊',
    'Invalid verification code': '驗證碼無效',
    'Verification code expired': '驗證碼已過期',
    'Account not found': '找不到帳戶',
    'Account already verified': '帳戶已驗證',
    'Invalid password': '密碼無效',
    'Password mismatch': '密碼不匹配',
    'Account locked': '帳戶已鎖定',
    'Session expired': '會話已過期',
    'Access denied': '存取被拒絕',
    'Unauthorized': '未授權',
    'Forbidden': '禁止存取',
    'Not found': '找不到',
    'Internal server error': '內部伺服器錯誤',
    'Service unavailable': '服務不可用',
    'Database connection error': '資料庫連線錯誤',
    'Constraint violation': '資料約束違反',
    'Duplicate entry': '重複的資料',
    'Foreign key constraint': '外鍵約束錯誤',
    'Unique constraint violation': '唯一約束違反',
    'Check constraint violation': '檢查約束違反',
    'Not null constraint violation': '非空約束違反',
    'Auth session missing!': '尚未取得重設連結授權，請從信內連結開啟本頁（建議用系統瀏覽器，勿用 Gmail 內建瀏覽器），或重新申請重設密碼'
  };

  // 檢查是否有完全匹配的錯誤信息
  if (errorMappings[message]) {
    return errorMappings[message];
  }

  // 檢查是否有部分匹配的錯誤信息
  for (const [englishError, chineseError] of Object.entries(errorMappings)) {
    if (message.toLowerCase().includes(englishError.toLowerCase())) {
      return chineseError;
    }
  }

  // 如果沒有找到匹配的錯誤信息，返回原始信息但添加中文前綴
  return `操作失敗：${message}`;
};

/** 郵件內驗證／重設連結的對外網址；生產請設 VITE_APP_URL 為正式網域，並在 Supabase Redirect URLs 加入對應路徑 */
function getAuthRedirectBaseUrl(): string {
  const envUrl = (import.meta.env.VITE_APP_URL as string | undefined)?.trim();
  if (envUrl) {
    return envUrl.replace(/\/$/, '');
  }
  return typeof window !== 'undefined' ? window.location.origin : '';
}

// 注册数据结构 - 移到类外部
interface RegistrationData {
  userType: 'customer' | 'craftsman' | 'company' | 'admin' | 'employee';
  basicInfo: {
    email: string;
    password: string;
    name: string;
    phone: string;
  };
  typeSpecificInfo: {
    // 客户特定信息
    preferredContactMethod?: string;
    emergencyContact?: object;
    
    // 个人工匠特定信息
    specialties?: number[];
    serviceAreas?: string[];
    description?: string;
    certificates?: string[];
    bankAccount?: object;
    hourlyRate?: number;
    
    // 公司特定信息
    companyName?: string;
    taxId?: string;
    businessLicense?: string;
    address?: string;
    website?: string;
    employeeCount?: number;
    establishedDate?: string;
    industry?: string;
    companySize?: string;
  };
}

/** 寫入 auth raw_user_meta_data；信箱驗證無 session 時由 handle_auth_user_upsert 讀取並寫入 companies/craftsmen（勿放銀行帳戶等敏感資料） */
function buildAuthUserMetadata(data: RegistrationData): Record<string, string> {
  const base: Record<string, string> = {
    name: data.basicInfo.name,
    user_type: data.userType,
    phone: data.basicInfo.phone || '',
  };
  const ts = data.typeSpecificInfo;
  if (data.userType === 'company') {
    base.reg_profile = JSON.stringify({
      kind: 'company',
      companyName: ts.companyName ?? '',
      taxId: ts.taxId ?? '',
      businessLicense: ts.businessLicense ?? '',
      address: ts.address ?? '',
      website: ts.website ?? '',
      employeeCount: ts.employeeCount ?? null,
      establishedDate: ts.establishedDate ?? null,
      industry: ts.industry ?? '',
      companySize: ts.companySize ?? '',
      specialties: ts.specialties ?? [],
      serviceAreas: ts.serviceAreas ?? [],
      description: ts.description ?? '',
      certificates: ts.certificates ?? [],
      hourlyRate: ts.hourlyRate ?? 0,
    });
  } else if (data.userType === 'craftsman') {
    base.reg_profile = JSON.stringify({
      kind: 'craftsman',
      specialties: ts.specialties ?? [],
      serviceAreas: ts.serviceAreas ?? [],
      description: ts.description ?? '',
      certificates: ts.certificates ?? [],
      hourlyRate: ts.hourlyRate ?? 0,
      companyName: ts.companyName ?? '',
      taxId: ts.taxId ?? '',
      address: ts.address ?? '',
    });
  }
  return base;
}

export class UserService {
  // 獲取當前用戶
  static async getCurrentUser(): Promise<User | null> {
    const isDev = import.meta.env.DEV;
    const startTime = performance.now();
    
    try {
      // 步驟 1: 獲取 Auth session 中的用戶
      const { data: { user: authUser }, error: authError } = await supabase.auth.getUser();
      
      if (authError) {
        if (isDev) {
          console.error('[UserService.getCurrentUser] Auth 錯誤:', authError);
        }
        return null;
      }
      
      if (!authUser) {
        if (isDev) {
          console.log('[UserService.getCurrentUser] 沒有 Auth session');
        }
        return null;
      }

      if (isDev) {
        console.log('[UserService.getCurrentUser] Auth 用戶 ID:', authUser.id, 'Email:', authUser.email);
      }

      // 步驟 2: 從 users 表查詢用戶記錄（優先使用 ID）
      let { data: userData, error: userError } = await supabase
        .from('users')
        .select('*')
        .eq('id', authUser.id)
        .maybeSingle();

      // 步驟 3: 如果通過 ID 找不到，嘗試通過 email 查詢
      if (userError || !userData) {
        if (isDev) {
          console.warn('[UserService.getCurrentUser] 通過 ID 找不到 users 表記錄，嘗試通過 email 查詢...');
        }
        
        if (authUser.email) {
          const { data: emailData, error: emailError } = await supabase
            .from('users')
            .select('*')
            .eq('email', authUser.email)
            .maybeSingle();
          
          if (emailError) {
            if (isDev) {
              console.error('[UserService.getCurrentUser] 通過 email 查詢失敗:', emailError);
            }
          } else if (emailData) {
            if (isDev) {
              console.log('[UserService.getCurrentUser] ✅ 通過 email 找到用戶記錄');
              if (emailData.id !== authUser.id) {
                console.warn('[UserService.getCurrentUser] ⚠️ ID 不匹配:', {
                  users_table_id: emailData.id,
                  auth_session_id: authUser.id
                });
              }
            }
            userData = emailData;
          }
        }
        
        // 步驟 4: 如果仍然找不到用戶記錄，記錄詳細錯誤信息
        if (!userData) {
          const errorDetails = {
            authUserId: authUser.id,
            authUserEmail: authUser.email,
            hasAuthSession: true,
            usersTableQuery: 'failed',
            suggestion: '該帳號可能尚未完成註冊流程，請確保用戶已通過註冊流程創建帳號'
          };
          
          console.error('[UserService.getCurrentUser] ❌ 用戶記錄不存在於 users 表中');
          console.error('[UserService.getCurrentUser] 錯誤詳情:', errorDetails);
          
          // 在開發環境中，提供診斷建議
          if (isDev) {
            console.group('[UserService.getCurrentUser] 診斷建議');
            console.log('1. 檢查 users 表中是否存在該用戶記錄');
            console.log('2. 檢查用戶是否已完成註冊流程');
            console.log('3. 檢查 RLS 策略是否允許查詢');
            console.log('4. 檢查用戶 ID 或 email 是否正確');
            console.groupEnd();
          }
          
          return null;
        }
      }

      if (!userData) {
        return null;
      }

      // 步驟 5: 處理 ID 不匹配的情況（優先使用 Auth session 的 ID）
      const finalUserId = userData.id !== authUser.id ? authUser.id : userData.id;
      
      if (userData.id !== authUser.id && isDev) {
        console.warn('[UserService.getCurrentUser] ⚠️ ID 不匹配，使用 Auth session ID:', {
          users_table_id: userData.id,
          auth_session_id: authUser.id,
          finalUserId
        });
      }

      // 步驟 6: 檢查用戶是否被列入黑名單
      try {
        await BlacklistGuard.assertUserAllowed(finalUserId);
      } catch (blacklistError) {
        if (isDev) {
          console.error('[UserService.getCurrentUser] 用戶被列入黑名單:', blacklistError);
        }
        return null;
      }

      // 步驟 7: 構建並返回用戶對象
      const user: User = {
        id: finalUserId,
        email: userData.email || authUser.email || '',
        name: userData.name || authUser.user_metadata?.name || authUser.email?.split('@')[0] || '用戶',
        phone: userData.phone || authUser.phone || '',
        userType: userData.user_type,
        profileImage: mapUserProfileImage(
          userData.profile_image,
          authUser.user_metadata?.avatar_url
        ),
        addresses: userData.addresses || [],
        isVerified: userData.is_verified || !!authUser.email_confirmed_at,
        createdAt: userData.created_at || authUser.created_at || new Date().toISOString(),
      };

      const duration = performance.now() - startTime;
      if (isDev) {
        console.log(`[UserService.getCurrentUser] ✅ 成功獲取用戶 (${duration.toFixed(2)}ms):`, {
          id: user.id,
          name: user.name,
          email: user.email,
          userType: user.userType
        });
      }

      return user;
    } catch (error) {
      const duration = performance.now() - startTime;
      console.error(`[UserService.getCurrentUser] ❌ 獲取用戶失敗 (${duration.toFixed(2)}ms):`, error);
      
      if (isDev && error instanceof Error) {
        console.error('[UserService.getCurrentUser] 錯誤堆棧:', error.stack);
      }
      
      return null;
    }
  }

  // 统一注册方法（needsEmailVerification：開啟信箱驗證時 signUp 通常無 session）
  static async register(data: RegistrationData): Promise<{
    user: User | null;
    error: string | null;
    needsEmailVerification?: boolean;
  }> {
    try {
      console.log('Starting registration for user type:', data.userType);
      
      // 验证基本信息
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(data.basicInfo.email)) {
        return { user: null, error: '請輸入有效的電子郵件地址' };
      }

      if (data.basicInfo.password.length < 6) {
        return { user: null, error: '密碼至少需要6個字符' };
      }

      // 检查邮箱是否已存在
      const { data: existingUser, error: existingUserError } = await supabase
        .from('users')
        .select('id, email')
        .eq('email', data.basicInfo.email);

      if (existingUserError) {
        console.error('檢查郵箱時發生錯誤:', existingUserError);
        return { user: null, error: '檢查郵箱時發生錯誤' };
      }

      if (existingUser && existingUser.length > 0) {
        return { user: null, error: '此電子郵件已被註冊，請使用其他郵箱或嘗試登入' };
      }

      // 创建 Auth 用户 - 使用链接验证
      // raw_user_meta_data 供 DB 觸發器 handle_auth_user_upsert 寫入 public.users（含 phone）並建立 customer 列
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: data.basicInfo.email,
        password: data.basicInfo.password,
        options: {
          data: buildAuthUserMetadata(data),
          emailRedirectTo: `${getAuthRedirectBaseUrl()}/auth`,
        }
      });

      if (authError) {
        console.error('Auth signup error:', authError);
        return { user: null, error: translateErrorMessage(authError.message) };
      }

      if (!authData.user) {
        return { user: null, error: '註冊失敗，請稍後再試' };
      }

      console.log('Auth user created successfully:', authData.user.id);

      const hasSession = !!authData.session;
      const needsEmailVerification = !hasSession;

      // public.users 一律由 handle_auth_user_upsert（auth 觸發器）建立／更新，避免客戶端 upsert 與 RLS／UPDATE 政策衝突。

      // 擴充資料表：僅在有 session 時以客戶端寫入（符合 RLS）。客戶無 session 時 customers 由觸發器建立。
      if (hasSession) {
        try {
          if (data.userType === 'customer') {
            await this.createCustomerProfile(authData.user.id, data.typeSpecificInfo);
          } else if (data.userType === 'craftsman') {
            await this.createCraftsmanProfile(authData.user.id, data.typeSpecificInfo);
          } else if (data.userType === 'company') {
            await this.createCompanyProfile(authData.user.id, data.typeSpecificInfo, data.basicInfo);
          }
        } catch (profileError) {
          console.error('Error creating profile:', profileError);
        }
      }

      // 返回用户信息
      const user: User = {
        id: authData.user.id,
        email: data.basicInfo.email,
        name: data.basicInfo.name,
        phone: data.basicInfo.phone || '',
        userType: data.userType,
        avatar: '',
        profileImage: '',
        addresses: [],
        isVerified: false, // 初始状态为未验证
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      return { user, error: null, needsEmailVerification };
    } catch (error) {
      console.error('Registration error:', error);
      return { user: null, error: '註冊過程中發生錯誤' };
    }
  }

  static async changePassword(email: string, currentPassword: string, newPassword: string): Promise<void> {
    // 先驗證目前密碼
    const { error: authError } = await supabase.auth.signInWithPassword({ email, password: currentPassword });
    if (authError) {
      throw new Error('目前密碼不正確');
    }
    // 更新為新密碼
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw new Error(translateErrorMessage(error.message));
  }

  static async updateSocialLinks(userId: string, links: any[]): Promise<void> {
    const { error } = await supabase
      .from('users')
      .update({ social_links: links, updated_at: new Date().toISOString() })
      .eq('id', userId);
    if (error) throw new Error(translateErrorMessage(error.message));
  }

  static async getSocialLinks(userId: string): Promise<any[]> {
    const { data, error } = await supabase
      .from('users')
      .select('social_links')
      .eq('id', userId)
      .single();
    if (error) throw new Error(translateErrorMessage(error.message));
    return data?.social_links || [];
  }

  // 创建客户资料
  private static async createCustomerProfile(userId: string, data: any): Promise<void> {
    const { data: existing, error: selErr } = await supabase
      .from('customers')
      .select('id')
      .eq('user_id', userId)
      .maybeSingle();

    if (selErr) {
      console.error('Error checking customer profile:', selErr);
      throw new Error('創建客戶資料失敗');
    }
    if (existing) {
      return;
    }

    const { error } = await supabase
      .from('customers')
      .insert({
        user_id: userId,
        preferred_contact_method: data.preferredContactMethod || 'email',
        created_at: new Date().toISOString()
      });

    if (error) {
      console.error('Error creating customer profile:', error);
      throw new Error('創建客戶資料失敗');
    }
  }

  // 创建工匠资料
  private static async createCraftsmanProfile(userId: string, data: any): Promise<void> {
    let defaultLocation = { lat: 25.0330, lng: 121.5654 }; // 默认台北市中心
    
    // 优先使用地址 geocode，如果没有地址则使用服务区域
    if (data.address && data.address.trim()) {
      try {
        // 使用 AddressService 进行地理编码
        const { AddressService } = await import('./addressService');
        const geocodeResult = await AddressService.geocodeAddress(data.address);
        if (geocodeResult) {
          defaultLocation = { lat: geocodeResult.lat, lng: geocodeResult.lng };
          console.log('✅ 注册时地址已 geocode，设置 location:', defaultLocation);
        } else {
          // geocode 失败，回退到服务区域
          console.warn('⚠️ 注册时地址 geocode 失败，使用服务区域');
          defaultLocation = data.serviceAreas && data.serviceAreas.length > 0
            ? this.getLocationFromServiceArea(data.serviceAreas[0])
            : defaultLocation;
        }
      } catch (error) {
        console.error('注册时地址地理编码失败，使用服务区域:', error);
        // geocode 失败，回退到服务区域
        defaultLocation = data.serviceAreas && data.serviceAreas.length > 0
          ? this.getLocationFromServiceArea(data.serviceAreas[0])
          : defaultLocation;
      }
    } else {
      // 没有地址，使用服务区域
      defaultLocation = data.serviceAreas && data.serviceAreas.length > 0
        ? this.getLocationFromServiceArea(data.serviceAreas[0])
        : defaultLocation;
    }

    const { error } = await supabase
      .from('craftsmen')
      .insert({
        user_id: userId,
        specialties: data.specialties || [],
        service_areas: data.serviceAreas || [],
        description: data.description || '',
        certificates: data.certificates || [],
        bank_account: data.bankAccount || null,
        hourly_rate: data.hourlyRate || 0,
        status: 'active',
        location: defaultLocation, // 添加默认 location
      });

    if (error) {
      console.error('Error creating craftsman profile:', error);
      throw new Error('創建工匠資料失敗');
    }
  }

  // 辅助方法：根据服务区域获取默认坐标
  private static getLocationFromServiceArea(area: string): { lat: number; lng: number } {
    // 台湾主要城市的坐标映射
    const cityCoordinates: Record<string, { lat: number; lng: number }> = {
      '台北市': { lat: 25.0330, lng: 121.5654 },
      '新北市': { lat: 25.0169, lng: 121.4637 },
      '桃園市': { lat: 24.9936, lng: 121.3010 },
      '台中市': { lat: 24.1477, lng: 120.6736 },
      '台南市': { lat: 22.9999, lng: 120.2269 },
      '高雄市': { lat: 22.6273, lng: 120.3014 },
      '基隆市': { lat: 25.1276, lng: 121.7395 },
      '新竹市': { lat: 24.8036, lng: 120.9686 },
      '嘉義市': { lat: 23.4801, lng: 120.4491 },
      // 可以添加更多城市...
    };

    return cityCoordinates[area] || { lat: 25.0330, lng: 121.5654 }; // 默认台北
  }

  // 创建公司资料
  private static async createCompanyProfile(userId: string, data: any, basicInfo?: any): Promise<void> {
    // 获取用户的 phone（从 basicInfo 或 users 表）
    let phone = basicInfo?.phone || '';
    if (!phone) {
      const { data: userData } = await supabase
        .from('users')
        .select('phone')
        .eq('id', userId)
        .single();
      phone = userData?.phone || '';
    }

    const { error } = await supabase
      .from('companies')
      .insert({
        user_id: userId,
        company_name: data.companyName || '',
        tax_id: data.taxId || '',
        business_license: data.businessLicense || null,
        phone: phone, // ✅ 添加 phone 字段
        address: data.address || null,
        website: data.website || null,
        employee_count: data.employeeCount || null,
        established_date: data.establishedDate || null,
        industry: data.industry || null,
        company_size: data.companySize || null,
        specialties: data.specialties || [],
        service_areas: data.serviceAreas || [],
        description: data.description || '',
        certificates: data.certificates || [],
        bank_account: data.bankAccount || null,
        hourly_rate: data.hourlyRate || 0,
        status: 'pending',
        created_at: new Date().toISOString()
      });

    if (error) {
      console.error('Error creating company profile:', error);
      throw new Error('創建公司資料失敗');
    }
  }

  // 注册服务工厂
  static createRegistrationService(userType: 'customer' | 'craftsman' | 'company' | 'admin') {
    switch (userType) {
      case 'customer':
        return new CustomerRegistrationService();
      case 'craftsman':
        return new CraftsmanRegistrationService();
      case 'company':
        return new CompanyRegistrationService();
      case 'admin':
        return new AdminRegistrationService();
      default:
        throw new Error('Invalid user type');
    }
  }

  // 用户登出
  static async logout(): Promise<void> {
    try {
      // 在登出前獲取用戶 ID，以便記錄活動
      let userId: string | null = null;
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const userData = await this.getCurrentUser();
          if (userData) {
            userId = userData.id;
          }
        }
      } catch (e) {
        console.warn('獲取用戶 ID 失敗，跳過活動記錄:', e);
      }

      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('Error logging out:', error);
      }

      // 記錄登出活動
      if (userId) {
        try {
          await ActivityService.logLogout(userId);
        } catch (activityError) {
          console.warn('記錄登出活動失敗:', activityError);
          // 不影響登出流程
        }
      }
    } catch (error) {
      console.error('Error logging out:', error);
    }
  }

  // 更新用户资料
  static async updateProfile(userId: string, userData: Partial<User>): Promise<{ success: boolean; error: string | null }> {
    try {
      const { error } = await supabase
        .from('users')
        .update({
          name: userData.name,
          phone: userData.phone,
          profile_image: userData.profileImage,
          addresses: userData.addresses,
          updated_at: new Date().toISOString(),
        })
        .eq('id', userId);

      if (error) {
        return { success: false, error: translateErrorMessage(error.message) };
      }

      return { success: true, error: null };
    } catch (error) {
      console.error('Error updating profile:', error);
      return { success: false, error: '更新失敗，請稍後再試' };
    }
  }

  // 本地登录方法（用于测试）- 現在會嘗試創建真實的 Supabase 用戶
  static async localLogin(email: string, password: string): Promise<User> {
    try {
      console.log('使用本地登录方法（嘗試創建真實用戶）...');
      
      // 根據郵箱地址判斷用戶類型
      let userType: 'customer' | 'craftsman' | 'company' | 'admin' | 'employee' = 'customer';
      let userName = 'feng';
      
      console.log('=== 用戶類型判斷 ===');
      console.log('郵箱:', email);
      
      // 明確的權限分離規則 - 精確匹配優先
      const emailLower = email.toLowerCase().trim();
      
      // 管理員權限 - 最高優先級
      if (emailLower.includes('admin') || emailLower === 'fengmanchanh890@gmail.com+admin') {
        userType = 'admin';
        userName = 'feng 管理員';
        console.log('匹配到管理員用戶');
      }
      // 公司權限
      else if (emailLower.includes('company') || emailLower.includes('fengcompany') || emailLower === 'fengmanchanh890@gmail.com+company') {
        userType = 'company';
        userName = 'feng 公司';
        console.log('匹配到公司用戶');
      }
      // 工匠權限
      else if (emailLower.includes('craftsman') || emailLower.includes('worker') || emailLower.includes('fengfix') || emailLower.includes('phongfix') || emailLower === 'fengmanchanh890@gmail.com+craftsman') {
        userType = 'craftsman';
        userName = 'feng 工匠';
        console.log('匹配到工匠用戶');
      }
      // 特定郵箱默認規則 - 支持通過 URL 參數或 localStorage 切換用戶類型
      else if (emailLower === 'fengmanchanh890@gmail.com') {
        // 嘗試從 localStorage 讀取用戶類型偏好
        const storedUserType = localStorage.getItem('fengmanchanh890_user_type');
        if (storedUserType && ['customer', 'craftsman', 'company', 'admin', 'employee'].includes(storedUserType)) {
          userType = storedUserType as 'customer' | 'craftsman' | 'company' | 'admin' | 'employee';
          console.log(`從 localStorage 讀取用戶類型: ${userType}`);
        }
        // 嘗試從 URL 參數讀取（如果在瀏覽器環境）
        else if (typeof window !== 'undefined') {
          const urlParams = new URLSearchParams(window.location.search);
          const urlUserType = urlParams.get('userType');
          if (urlUserType && ['customer', 'craftsman', 'company', 'admin', 'employee'].includes(urlUserType)) {
            userType = urlUserType as 'customer' | 'craftsman' | 'company' | 'admin' | 'employee';
            // 保存到 localStorage
            localStorage.setItem('fengmanchanh890_user_type', urlUserType);
            console.log(`從 URL 參數讀取用戶類型: ${userType}`);
          } else {
            // 默認作為客戶
            userType = 'customer';
            userName = 'feng 客戶';
            console.log('匹配到客戶用戶 (fengmanchanh890@gmail.com) - 使用默認類型');
          }
        } else {
          userType = 'customer';
          userName = 'feng 客戶';
          console.log('匹配到客戶用戶 (fengmanchanh890@gmail.com)');
        }
        
        // 根據最終用戶類型設置名稱
        switch (userType) {
          case 'admin':
            userName = 'feng 管理員';
            break;
          case 'company':
            userName = 'feng 公司';
            break;
          case 'craftsman':
            userName = 'feng 工匠';
            break;
          case 'customer':
          default:
            userName = 'feng 客戶';
            break;
        }
      }
      // 客戶權限
      else if (emailLower.includes('customer') || emailLower.includes('feng890')) {
        userType = 'customer';
        userName = 'feng 客戶';
        console.log('匹配到客戶用戶');
      }
      // 默認客戶
      else {
        userType = 'customer';
        userName = email.split('@')[0] || '用戶'; // 使用郵箱用戶名部分
        console.log('未匹配到特定類型，默認為客戶');
      }
      
      console.log('最終用戶類型:', userType);
      console.log('==================');
      
      // 嘗試在 Supabase 中查找或創建真實用戶
      console.log('嘗試在 Supabase 中查找或創建用戶...');
      
      // 1. 先檢查 users 表中是否已存在該郵箱
      const { data: existingUser, error: checkError } = await supabase
        .from('users')
        .select('id, email, name, phone, user_type, profile_image, is_verified, created_at, updated_at, addresses')
        .eq('email', email)
        .maybeSingle();
      
      let userId: string;
      let userData: any;
      
      if (existingUser) {
        // 用戶已存在，使用現有用戶
        console.log('找到現有用戶，使用真實用戶ID:', existingUser.id);
        userId = existingUser.id;
        userData = existingUser;
        
        // 不覆蓋用戶類型，使用資料庫中的類型
        console.log(`使用資料庫中的用戶類型: ${userData.user_type}`);
        
        // ✅ 添加：嘗試登入以創建 Auth session
        // 嘗試多個可能的密碼（員工創建時使用 'tempPassword123!'）
        const possiblePasswords = [
          password,  // 用戶提供的密碼
          'tempPassword123!',  // 員工創建時使用的默認密碼
          'TempPassword123!',  // 備選密碼（大寫 T）
        ].filter(Boolean); // 過濾掉 undefined/null
        
        // 對於員工、客服、行政、財務和其他用戶，優先嘗試默認密碼
        if (userData.user_type === 'employee' || 
            userData.user_type === 'customer_service' || 
            userData.user_type === 'administrative_staff' ||
            userData.user_type === 'finance_staff' ||
            userData.user_type === 'other_staff') {
          possiblePasswords.unshift('tempPassword123!'); // 將默認密碼放在最前面
        }
        
        // ✅ 新增：如果是員工類型，檢查 employees 表以驗證部門和用戶類型一致性
        if (userData.user_type === 'employee' || 
            userData.user_type === 'customer_service' || 
            userData.user_type === 'administrative_staff' ||
            userData.user_type === 'finance_staff' ||
            userData.user_type === 'other_staff') {
          // 查詢 employees 表，使用 email 匹配（因為可能 user_id 為空）
          const { data: employeeData } = await supabase
            .from('employees')
            .select('department, user_id')
            .or(`user_id.eq.${userId},email.eq.${email}`)
            .maybeSingle();
          
          if (employeeData) {
            console.log(`員工部門: ${employeeData.department}`);
            
            // 檢查用戶類型是否與部門匹配
            let expectedUserType: 'employee' | 'customer_service' | 'administrative_staff' | 'finance_staff' | 'other_staff' = 'employee';
            if (employeeData.department === '客服部') {
              expectedUserType = 'customer_service';
            } else if (employeeData.department === '行政部') {
              expectedUserType = 'administrative_staff';
            } else if (employeeData.department === '財務部') {
              expectedUserType = 'finance_staff';
            } else if (employeeData.department === '其他') {
              expectedUserType = 'other_staff';
            }
            
            if (userData.user_type !== expectedUserType) {
              console.log(`⚠️ 用戶類型不匹配！當前: ${userData.user_type}，應為: ${expectedUserType}，正在更新...`);
              // 更新用戶類型以匹配部門
              const { error: updateTypeError } = await supabase
                .from('users')
                .update({
                  user_type: expectedUserType,
                  updated_at: new Date().toISOString()
                })
                .eq('id', userId);
              
              if (!updateTypeError) {
                userData.user_type = expectedUserType;
                console.log(`✅ 已更新用戶類型為: ${expectedUserType}`);
              } else {
                console.warn(`更新用戶類型失敗: ${updateTypeError.message}`);
              }
            }
          }
        }
        
        let sessionCreated = false;
        let lastSignInError = null;
        
        for (const tryPassword of possiblePasswords) {
          try {
            console.log(`嘗試使用密碼登入: ${tryPassword ? '***' : 'null'}`);
            const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
              email: email,
              password: tryPassword
            });
            
            if (signInError) {
              // 特殊處理：如果 Email 登入被禁用，跳過 Auth 認證
              if (signInError.message.includes('Email logins are disabled')) {
                console.error('❌ Email/Password 登入已被禁用');
                console.error('📋 請在 Supabase Dashboard 中啟用 Email/Password 登入');
                console.error('   路徑：Authentication → Providers → Email → Enable');
                console.warn('⚠️ 無法創建 Auth session，RLS 策略可能無法正常工作');
                // 不設置 sessionCreated，繼續使用 users 表數據
                break; // 跳出循環，直接使用 users 表數據創建用戶對象
              }
              
              // 特殊處理：如果是員工、客服、行政、財務或其他且郵箱未確認
              if (signInError.message.includes('Email not confirmed') && 
                  (userData.user_type === 'employee' || 
                   userData.user_type === 'customer_service' || 
                   userData.user_type === 'administrative_staff' ||
                   userData.user_type === 'finance_staff' ||
                   userData.user_type === 'other_staff')) {
                console.log('⚠️ 員工郵箱未確認，嘗試重新發送驗證郵件...');
                try {
                  // 嘗試重新發送驗證郵件（這不需要 admin 權限）
                  const { error: resendError } = await supabase.auth.resend({
                    type: 'signup',
                    email: email,
                  });
                  
                  if (!resendError) {
                    console.log('✅ 已重新發送驗證郵件，請檢查您的郵箱');
                    // 拋出明確的錯誤，讓上層處理
                    throw new Error('EMAIL_NOT_CONFIRMED');
                  } else {
                    console.warn('無法重新發送驗證郵件:', resendError);
                    // 嘗試使用 admin API（需要服務角色，通常在後端）
                    console.log('⚠️ 嘗試使用 admin API 確認郵箱（需要服務角色）...');
                    try {
                      const { error: updateError } = await supabase.auth.admin.updateUserById(
                        userId,
                        { email_confirm: true }
                      );
                      
                      if (!updateError) {
                        console.log('✅ 成功確認員工郵箱，重新嘗試登入...');
                        // 重新嘗試登入
                        const { data: retrySignIn, error: retryError } = await supabase.auth.signInWithPassword({
                          email: email,
                          password: tryPassword
                        });
                        
                        if (!retryError && retrySignIn?.user?.id) {
                          console.log('✅ 郵箱確認後登入成功，用戶ID:', retrySignIn.user.id);
                          const { data: { session } } = await supabase.auth.getSession();
                          if (session) {
                            console.log('✅ 確認 session 已建立');
                            sessionCreated = true;
                            break;
                          }
                        } else {
                          console.warn('郵箱確認後登入仍然失敗:', retryError);
                        }
                      } else {
                        console.warn('無法使用 admin API 確認郵箱（需要服務角色，請在 Supabase Dashboard 中手動確認）:', updateError);
                        throw new Error('EMAIL_NOT_CONFIRMED');
                      }
                    } catch (adminErr: any) {
                      console.warn('Admin API 不可用（需要服務角色）:', adminErr);
                      console.warn('💡 解決方案：');
                      console.warn('   1. 在 Supabase Dashboard → Authentication → Users 中手動確認郵箱');
                      console.warn('   2. 或使用後端 Edge Function 配合服務角色來確認郵箱');
                      throw new Error('EMAIL_NOT_CONFIRMED');
                    }
                  }
                } catch (resendErr: any) {
                  // 如果重新發送也失敗，直接拋出錯誤
                  if (resendErr.message === 'EMAIL_NOT_CONFIRMED') {
                    throw resendErr;
                  }
                  console.warn('重新發送驗證郵件失敗:', resendErr);
                  throw new Error('EMAIL_NOT_CONFIRMED');
                }
              }
              
              console.warn(`登入失敗（密碼: ${tryPassword ? '***' : 'null'}）:`, signInError.message);
              lastSignInError = signInError;
              
              // 如果已經處理了郵箱確認問題，不再繼續嘗試下一個密碼
              if (sessionCreated) {
                break;
              }
              
              continue; // 嘗試下一個密碼
            } else if (signInData?.user?.id) {
              console.log('✅ 成功創建 Auth session，用戶ID:', signInData.user.id);
              // 確認 session 已建立
              const { data: { session } } = await supabase.auth.getSession();
              if (session) {
                console.log('✅ 確認 session 已建立，session ID:', session.access_token.substring(0, 20) + '...');
                sessionCreated = true;
                break; // 成功，退出循環
              } else {
                console.warn('⚠️ signInWithPassword 成功但 session 為空');
              }
            }
          } catch (signInErr: any) {
            console.error('嘗試登入時發生錯誤:', signInErr);
            // 如果是 EMAIL_NOT_CONFIRMED 錯誤，直接向上拋出
            if (signInErr?.message === 'EMAIL_NOT_CONFIRMED') {
              throw signInErr;
            }
            lastSignInError = signInErr as any;
          }
        }
        
        if (!sessionCreated) {
          console.error('❌ 所有密碼嘗試都失敗，無法創建 Auth session');
          console.error('最後的錯誤:', lastSignInError);
          
          // 檢查用戶是否在 Auth 系統中存在
          try {
            const { data: authUser, error: authUserError } = await supabase.auth.admin.getUserById(userId);
            if (authUserError || !authUser) {
              console.warn('⚠️ 該用戶在 Supabase Auth 系統中不存在');
              console.warn('💡 這可能是因為：');
              console.warn('   1. 用戶記錄只在 users 表中，沒有對應的 Auth 用戶');
              console.warn('   2. 需要重新創建 Auth 用戶或重置密碼');
              console.warn('   3. 對於員工帳號，默認密碼應該是: tempPassword123!');
            } else {
              console.warn('⚠️ 用戶在 Auth 系統中存在，但密碼不匹配');
              console.warn('💡 建議：');
              console.warn('   1. 確認密碼是否正確（員工默認密碼: tempPassword123!)');
              console.warn('   2. 或聯繫管理員重置密碼');
            }
          } catch (adminErr) {
            // Admin API 不可用，跳過檢查
            console.warn('⚠️ 無法檢查 Auth 用戶狀態（Admin API 需要服務角色）');
          }
          
          console.warn('⚠️ 警告：沒有 Auth session，RLS 策略將無法正常工作');
          console.warn('💡 當前將使用本地登入方式，但某些功能可能受限');
        }
      } else {
        // 用戶不存在，嘗試創建 Auth 用戶和 users 記錄
        console.log('用戶不存在，嘗試創建真實用戶...');
        
        try {
          // 創建 Auth 用戶（使用提供的密碼）
          const { data: authData, error: authError } = await supabase.auth.signUp({
            email: email,
            password: password || 'TempPassword123!', // 如果沒有密碼，使用臨時密碼
            options: {
              data: {
                name: userName,
                user_type: userType
              },
              emailRedirectTo: `${getAuthRedirectBaseUrl()}/auth`
            }
          });
          
          if (authError) {
            // 如果創建失敗（可能是郵箱已存在於 auth），嘗試查找
            console.log('Auth 創建失敗，嘗試查找:', authError.message);
            
            if (authError.message.includes('already registered') || authError.message.includes('already exists')) {
              // 郵箱已在 auth 中，嘗試直接登入獲取用戶ID
              try {
                const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
                  email: email,
                  password: password || 'TempPassword123!'
                });
                
                if (signInData?.user?.id) {
                  userId = signInData.user.id;
                  console.log('通過登入獲取真實用戶ID:', userId);
                } else {
                  throw new Error('無法獲取用戶ID');
                }
              } catch (signInErr) {
                console.error('登入也失敗，回退到臨時用戶:', signInErr);
                throw new Error('無法創建或查找用戶');
              }
            } else {
              throw authError;
            }
          } else if (authData?.user?.id) {
            userId = authData.user.id;
            console.log('成功創建真實用戶ID:', userId);
          } else {
            throw new Error('創建用戶失敗，未獲取到用戶ID');
          }
          
          // 創建 users 表記錄
          const { error: userInsertError } = await supabase
            .from('users')
            .insert({
              id: userId,
              name: userName,
              email: email,
              phone: '',
              user_type: userType,
              profile_image: '',
              is_verified: true // 本地創建的用戶視為已驗證
            });
          
          if (userInsertError) {
            // 如果插入失敗（可能是並發創建），嘗試查找
            console.log('插入 users 表失敗，嘗試查找:', userInsertError.message);
            const { data: foundUser } = await supabase
              .from('users')
              .select('*')
              .eq('id', userId)
              .maybeSingle();
            
            if (foundUser) {
              userData = foundUser;
            } else {
              throw userInsertError;
            }
          } else {
            // 查詢剛創建的用戶
            const { data: newUser, error: fetchError } = await supabase
              .from('users')
              .select('*')
              .eq('id', userId)
              .single();
            
            if (fetchError || !newUser) {
              throw new Error('創建用戶後無法查詢用戶資料');
            }
            
            userData = newUser;
          }
        } catch (createError: any) {
          console.error('創建真實用戶失敗，回退到臨時用戶:', createError);
          // 如果創建失敗，回退到臨時用戶（但這不會有真實數據）
          const user: User = {
            id: 'temp-user-id',
            email: email,
            name: userName,
            phone: '',
            profileImage: '',
            addresses: [],
            isVerified: true,
            createdAt: new Date().toISOString(),
            userType: userType,
          };
          
          console.log('使用臨時用戶（無法創建真實用戶）:', user);
          return user;
        }
      }
      
      // 由於本地登入不會建立 Supabase Session，直接使用資料表中的 ID
      const finalUserId = userData.id;

      // 構建真實用戶對象
      const user: User = {
        id: finalUserId,
        email: userData.email || email,
        name: userData.name || userName,
        phone: userData.phone || '',
        userType: userData.user_type || userType,
        profileImage: userData.profile_image || '',
        addresses: userData.addresses || [],
        isVerified: !!userData.is_verified,
        createdAt: userData.created_at || new Date().toISOString(),
        updatedAt: userData.updated_at || new Date().toISOString(),
      };
      
      console.log('本地登入成功（使用真實用戶）:', user);
      return user;
    } catch (error) {
      console.error('本地登入錯誤:', error);
      throw error;
    }
  }

  // 修改login方法，处理邮箱未验证的情况
  static async login(email: string, password: string): Promise<User> {
    try {
      console.log('UserService.login 開始...');
      
      // 使用 Supabase 認證
      console.log('調用 supabase.auth.signInWithPassword...');
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      console.log('supabase.auth.signInWithPassword 完成', { data, error });

      if (error) {
        console.error('Supabase 認證錯誤:', error);
        
        // 特殊處理：如果 Email 登入被禁用，提供詳細的錯誤信息和解決方案
        if (error.message.includes('Email logins are disabled')) {
          console.error('❌ Email/Password 登入已被禁用');
          console.error('📋 解決方案：請在 Supabase Dashboard 中啟用 Email/Password 登入');
          console.error('📋 步驟：');
          console.error('   1. 前往 https://supabase.com/dashboard');
          console.error('   2. 選擇您的專案');
          console.error('   3. 進入 Authentication → Providers');
          console.error('   4. 找到 "Email" provider');
          console.error('   5. 點擊 "Enable" 按鈕');
          console.error('   6. 保存設置');
          console.error('   7. 重新嘗試登入');
          console.warn('⚠️ 當前使用本地登入方式（無 Auth session，RLS 可能無法正常工作）');
          console.warn('⚠️ 這是一個臨時解決方案，請盡快啟用 Email/Password 登入');
          
          // 安全修復：移除 localLogin fallback
          // 如果 Email 登入被禁用，直接拋出錯誤，要求用戶在 Supabase Dashboard 中啟用
          console.error('❌ Email/Password 登入已被禁用');
          console.error('📋 請在 Supabase Dashboard 中啟用 Email/Password 登入');
          console.error('📋 步驟：');
          console.error('   1. 前往 https://supabase.com/dashboard');
          console.error('   2. 選擇您的專案');
          console.error('   3. 進入 Authentication → Providers');
          console.error('   4. 找到 "Email" provider');
          console.error('   5. 點擊 "Enable" 按鈕');
          console.error('   6. 保存設置');
          throw new Error('EMAIL_LOGIN_DISABLED');
        }
        
        // 特殊處理郵箱未確認的情況
        if (error.message.includes('Email not confirmed')) {
          throw new Error('EMAIL_NOT_CONFIRMED'); // 使用特殊錯誤代碼
        }
        
        throw new Error(translateErrorMessage(error.message));
      }

      if (!data || !data.user) {
        console.error('沒有獲取到用戶數據:', data);
        throw new Error('登入失敗，未獲取到用戶信息');
      }

      if (!data.session) {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          console.error('signInWithPassword 成功但未建立 session');
          throw new Error('登入未能建立工作階段，請重試');
        }
      }

      // 檢查郵箱是否已確認
      if (!data.user.email_confirmed_at) {
        console.log('郵箱尚未確認');
        throw new Error('EMAIL_NOT_CONFIRMED');
      }

      console.log('Supabase 認證成功，獲取用戶資料...');

      // 獲取用戶的完整資料
      console.log('查詢用戶資料，用戶ID:', data.user.id);
      let { data: userData, error: userFetchError } = await supabase
        .from('users')
        .select('*')
        .eq('id', data.user.id)
        .maybeSingle(); // 使用 maybeSingle 避免不存在的錯誤

      // 檢查是否是 RLS 策略錯誤（500 錯誤或無限遞迴）
      if (userFetchError) {
        const errorCode = userFetchError.code;
        const errorMessage = userFetchError.message || '';
        
        // 檢查是否是 RLS 或無限遞迴相關的錯誤
        if (
          errorCode === '42501' || // permission denied
          errorCode === 'P0001' || // raise exception (可能包含無限遞迴)
          errorMessage.includes('infinite recursion') ||
          errorMessage.includes('recursion') ||
          errorMessage.includes('policy') ||
          (errorCode && errorCode.startsWith('5')) // 5xx 錯誤
        ) {
          console.error('❌ RLS 策略錯誤，無法查詢用戶記錄:', {
            code: errorCode,
            message: errorMessage,
            details: userFetchError.details,
            hint: userFetchError.hint
          });
          console.error('📋 這可能是 RLS 策略無限遞迴問題');
          console.error('📋 請執行最新的 migration 來修復 RLS 策略');
          
          // 登出 Auth session
          await supabase.auth.signOut();
          
          throw new Error('資料庫權限錯誤，請聯繫管理員。錯誤代碼: ' + (errorCode || 'UNKNOWN'));
        }
      }

      // 如果找不到用戶記錄，也嘗試通過 email 查詢（處理 ID 不匹配的情況）
      if (!userData && data.user.email && !userFetchError) {
        console.log('⚠️ 通過 ID 找不到用戶記錄，嘗試通過 email 查詢...');
        const { data: emailData, error: emailError } = await supabase
          .from('users')
          .select('*')
          .eq('email', data.user.email)
          .maybeSingle();
        
        // 檢查 email 查詢是否也有 RLS 錯誤
        if (emailError) {
          const errorCode = emailError.code;
          const errorMessage = emailError.message || '';
          
          if (
            errorCode === '42501' ||
            errorCode === 'P0001' ||
            errorMessage.includes('infinite recursion') ||
            errorMessage.includes('recursion') ||
            errorMessage.includes('policy') ||
            (errorCode && errorCode.startsWith('5'))
          ) {
            console.error('❌ RLS 策略錯誤（email 查詢）:', {
              code: errorCode,
              message: errorMessage
            });
            await supabase.auth.signOut();
            throw new Error('資料庫權限錯誤，請聯繫管理員。錯誤代碼: ' + (errorCode || 'UNKNOWN'));
          }
        }
        
        if (emailData) {
          console.log('✅ login: 通過 email 找到用戶記錄，ID:', emailData.id);
          userData = emailData;
          
          // 如果找到的記錄 ID 與 Auth session 的 ID 不一致，記錄警告
          if (emailData.id !== data.user.id) {
            console.warn('⚠️ login: 用戶記錄 ID 不匹配', {
              users_table_id: emailData.id,
              auth_session_id: data.user.id
            });
          }
        }
      }

      // 如果仍然找不到用戶記錄，嘗試自動創建
      if (!userData && !userFetchError && data.user) {
        console.warn('⚠️ 用戶記錄不存在於 users 表中，嘗試自動創建...');
        console.log('📋 Auth 用戶信息:', {
          id: data.user.id,
          email: data.user.email,
          email_confirmed: !!data.user.email_confirmed_at
        });
        
        // 自動創建用戶記錄
        const defaultName = data.user.email?.split('@')[0] || '用戶';
        
        // 優先使用 Auth metadata 的 user_type，避免工匠註冊在 users 缺失時被誤建為 customer
        const userEmail = (data.user.email || email).toLowerCase();
        const metaUserType = (data.user.user_metadata?.user_type || '').toString().toLowerCase();
        let inferredUserType: 'customer' | 'craftsman' | 'company' | 'admin' = 'customer';

        if (metaUserType === 'company' || metaUserType === 'craftsman' || metaUserType === 'admin' || metaUserType === 'customer') {
          inferredUserType = metaUserType as 'customer' | 'craftsman' | 'company' | 'admin';
          console.log('📋 使用 Auth metadata 用戶類型:', inferredUserType);
        } else if (userEmail.includes('company') || userEmail.includes('corp') || userEmail.includes('enterprise')) {
          inferredUserType = 'company';
          console.log('📋 metadata 缺失，根據郵箱判斷為公司類型:', userEmail);
        } else if (userEmail.includes('craftsman') || userEmail.includes('master') || userEmail.includes('technician')) {
          inferredUserType = 'craftsman';
          console.log('📋 metadata 缺失，根據郵箱判斷為工匠類型:', userEmail);
        } else if (userEmail.includes('admin') || userEmail.includes('administrator')) {
          inferredUserType = 'admin';
          console.log('📋 metadata 缺失，根據郵箱判斷為管理員類型:', userEmail);
        } else {
          inferredUserType = 'customer';
          console.log('📋 metadata 缺失，使用默認客戶類型:', userEmail);
        }
        
        const { data: newUserData, error: createError } = await supabase
          .from('users')
          .insert({
            id: data.user.id,
            email: data.user.email || email,
            name: defaultName,
            user_type: inferredUserType, // 根據郵箱智能判斷用戶類型
            is_verified: !!data.user.email_confirmed_at,
            phone: '',
            profile_image: ''
          })
          .select()
          .single();
        
        if (createError) {
          console.error('❌ 自動創建用戶記錄失敗:', createError);
          console.error('📋 錯誤詳情:', {
            code: createError.code,
            message: createError.message,
            details: createError.details,
            hint: createError.hint
          });
          
          // 登出 Auth session
          await supabase.auth.signOut();
          
          throw new Error('此帳號尚未註冊，請先完成註冊流程。如果問題持續存在，請聯繫管理員。');
        }
        
        console.log('✅ 自動創建用戶記錄成功:', newUserData);
        userData = newUserData;
      } else if (!userData && !userFetchError) {
        // 如果沒有 Auth 用戶信息，拒絕登入
        console.error('❌ 登入失敗：用戶記錄不存在於 users 表中，且無法獲取 Auth 用戶信息');
        await supabase.auth.signOut();
        throw new Error('此帳號尚未註冊，請先完成註冊流程。如果問題持續存在，請聯繫管理員。');
      }
      
      // 如果有錯誤但不是 RLS 錯誤，也拒絕登入
      if (userFetchError && !userData) {
        console.error('❌ 查詢用戶記錄時發生錯誤:', userFetchError);
        await supabase.auth.signOut();
        throw new Error('無法獲取用戶資料，請稍後再試。錯誤: ' + (userFetchError.message || 'UNKNOWN'));
      }

      console.log('用戶資料獲取成功:', userData);

      // 檢查並自動修正用戶類型（如果郵箱包含關鍵字但類型不匹配）
      const userEmail = (userData.email || email).toLowerCase();
      let correctedUserType = userData.user_type;
      let needsUpdate = false;
      
      if (userEmail.includes('company') || userEmail.includes('corp') || userEmail.includes('enterprise')) {
        if (userData.user_type !== 'company') {
          correctedUserType = 'company';
          needsUpdate = true;
          console.log('⚠️ 檢測到郵箱包含 "company" 但用戶類型為', userData.user_type, '，將更新為 company');
        }
      } else if (userEmail.includes('craftsman') || userEmail.includes('master') || userEmail.includes('technician')) {
        if (userData.user_type !== 'craftsman') {
          correctedUserType = 'craftsman';
          needsUpdate = true;
          console.log('⚠️ 檢測到郵箱包含 "craftsman" 但用戶類型為', userData.user_type, '，將更新為 craftsman');
        }
      } else if (userEmail.includes('admin') || userEmail.includes('administrator')) {
        if (userData.user_type !== 'admin') {
          correctedUserType = 'admin';
          needsUpdate = true;
          console.log('⚠️ 檢測到郵箱包含 "admin" 但用戶類型為', userData.user_type, '，將更新為 admin');
        }
      }
      
      // 如果需要更新，嘗試更新用戶類型
      if (needsUpdate) {
        console.log('📋 嘗試更新用戶類型從', userData.user_type, '到', correctedUserType);
        const { error: updateError } = await supabase
          .from('users')
          .update({
            user_type: correctedUserType,
            updated_at: new Date().toISOString()
          })
          .eq('id', userData.id);
        
        if (updateError) {
          console.warn('⚠️ 更新用戶類型失敗:', updateError);
          console.warn('📋 將使用原始用戶類型:', userData.user_type);
        } else {
          console.log('✅ 成功更新用戶類型為:', correctedUserType);
          userData.user_type = correctedUserType;
        }
      }

      const user: User = {
        id: userData.id,
        email: userData.email,
        name: userData.name,
        phone: userData.phone,
        userType: userData.user_type,
        profileImage: mapUserProfileImage(userData.profile_image),
        addresses: userData.addresses || [],
        isVerified: !!userData.is_verified,
        createdAt: userData.created_at,
        updatedAt: userData.updated_at,
      };

      try {
        await BlacklistGuard.assertUserAllowed(user.id);
      } catch (guardError) {
        await supabase.auth.signOut();
        throw guardError;
      }

      const { data: { session: persistedSession } } = await supabase.auth.getSession();
      if (!persistedSession) {
        await supabase.auth.signOut();
        throw new Error('登入未能保存工作階段，請重試');
      }

      console.log('UserService.login 完成，返回用戶:', user);

      // 記錄登入活動
      try {
        await ActivityService.logLogin(user.id, {
          ipAddress: null, // 可以從請求中獲取
          location: null, // 可以從 IP 獲取
        });
      } catch (activityError) {
        console.warn('記錄登入活動失敗:', activityError);
        // 不影響登入流程
      }
      
      return user;
    } catch (error: any) {
      console.error('UserService.login 錯誤:', error);
      
      // 如果是 EMAIL_NOT_CONFIRMED 錯誤，直接拋出，不嘗試本地登入
      if (error?.message === 'EMAIL_NOT_CONFIRMED') {
        throw error;
      }
      
      // 安全修復：移除 localLogin fallback，防止未驗證的登入
      // 如果 Supabase 認證失敗，直接拋出錯誤，不嘗試本地登入
      console.error('❌ Supabase 認證失敗，拒絕登入');
      console.error('📋 這是為了安全考慮，所有登入都必須通過 Supabase Auth 驗證');
      throw error; // 拋出原始錯誤
    }
  }

  // 在 UserService 類中添加這個方法
  static async registerCraftsman(craftsmanData: {
    email: string;
    password: string;
    name: string;
    phone: string;
    companyName?: string;
    taxId?: string;
    specialties: number[];
    serviceAreas: string[];
    description: string;
    certificates: string[];
    bankAccount: {
      bankCode: string;
      accountNumber: string;
      accountName: string;
    };
    hourlyRate: number;
  }): Promise<{
    user: User | null;
    error: string | null;
    needsEmailVerification?: boolean;
  }> {
    const registrationData: RegistrationData = {
      userType: 'craftsman',
      basicInfo: {
        email: craftsmanData.email,
        password: craftsmanData.password,
        name: craftsmanData.name,
        phone: craftsmanData.phone,
      },
      typeSpecificInfo: {
        specialties: craftsmanData.specialties,
        serviceAreas: craftsmanData.serviceAreas,
        description: craftsmanData.description,
        certificates: craftsmanData.certificates,
        bankAccount: craftsmanData.bankAccount,
        hourlyRate: craftsmanData.hourlyRate,
      },
    };

    return await this.register(registrationData);
  }

  // 在 UserService 類中添加 registerCompany 方法
  static async registerCompany(companyData: {
    email: string;
    password: string;
    name: string;
    phone: string;
    companyName?: string;
    taxId?: string;
    businessLicense?: string; // ✅ 添加 businessLicense 字段
    address?: string;
    website?: string;
    employeeCount?: number;
    establishedDate?: string;
    specialties: number[];
    serviceAreas: string[];
    description: string;
    certificates: string[];
    bankAccount: {
      bankCode: string;
      accountNumber: string;
      accountName: string;
    };
    hourlyRate?: number; // 公司註冊時薪為可選
  }): Promise<{
    user: User | null;
    error: string | null;
    needsEmailVerification?: boolean;
  }> {
    const registrationData: RegistrationData = {
      userType: 'company', // 設置為公司類型
      basicInfo: {
        email: companyData.email,
        password: companyData.password,
        name: companyData.name,
        phone: companyData.phone,
      },
      typeSpecificInfo: {
        companyName: companyData.companyName,
        taxId: companyData.taxId,
        businessLicense: companyData.businessLicense, // ✅ 添加 businessLicense 传递
        address: companyData.address,
        website: companyData.website,
        employeeCount: companyData.employeeCount,
        establishedDate: companyData.establishedDate,
        specialties: companyData.specialties,
        serviceAreas: companyData.serviceAreas,
        description: companyData.description,
        certificates: companyData.certificates,
        bankAccount: companyData.bankAccount,
        hourlyRate: companyData.hourlyRate,
      },
    };

    return await this.register(registrationData);
  }

  // 修改 getNearbyCraftsmen 方法
  static async getNearbyCraftsmen(location: { lat: number; lng: number }, radius: number = 10) {
    try {
      // 使用簡單的距離計算而不是 PostGIS
      const { data, error } = await supabase
        .from('craftsmen')
        .select(`
          *,
          users!craftsmen_user_id_fkey!inner(name, email, phone)
        `)
        .eq('status', 'active')
        .not('location', 'is', null);

      if (error) {
        console.error('Error fetching craftsmen:', error);
        return [];
      }

      // 在 JavaScript 中計算距離
      const craftsmenWithDistance = data
        .map(craftsman => {
          if (!craftsman.location) return null;
          
          const distance = this.calculateDistance(
            location.lat,
            location.lng,
            craftsman.location.lat,
            craftsman.location.lng
          );
          
          return {
            ...craftsman,
            distance: Math.round(distance * 100) / 100 // 保留兩位小數
          };
        })
        .filter(craftsman => craftsman && craftsman.distance <= radius)
        .sort((a, b) => a!.distance - b!.distance);

      return craftsmenWithDistance;
    } catch (error) {
      console.error('Error in getNearbyCraftsmen:', error);
      return [];
    }
  }

  // 添加距離計算方法
  private static calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371; // 地球半徑（公里）
    const dLat = this.deg2rad(lat2 - lat1);
    const dLon = this.deg2rad(lon2 - lon1);
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(this.deg2rad(lat1)) * Math.cos(this.deg2rad(lat2)) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    const distance = R * c; // 距離（公里）
    return distance;
  }

  private static deg2rad(deg: number): number {
    return deg * (Math.PI/180);
  }

  // 忘記密碼 - 發送重置密碼郵件
  static async forgotPassword(email: string): Promise<{ success: boolean; error: string | null }> {
    try {
      console.log('發送密碼重置郵件到:', email);
      
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${getAuthRedirectBaseUrl()}/auth?mode=reset-password`,
      });

      if (error) {
        console.error('發送密碼重置郵件錯誤:', error);
        return { success: false, error: translateErrorMessage(error.message) };
      }

      console.log('密碼重置郵件發送成功');
      return { success: true, error: null };
    } catch (error) {
      console.error('忘記密碼錯誤:', error);
      return { success: false, error: '發送密碼重置郵件失敗，請稍後再試' };
    }
  }

  // 重置密碼
  static async resetPassword(newPassword: string): Promise<{ success: boolean; error: string | null }> {
    try {
      console.log('重置密碼...');

      const runUpdate = () => supabase.auth.updateUser({ password: newPassword });

      let { error } = await runUpdate();

      const isSessionMissing = (e: typeof error) =>
        !!e &&
        (e.message?.toLowerCase().includes('session missing') ||
          (e as { name?: string }).name === 'AuthSessionMissingError');

      if (isSessionMissing(error)) {
        await new Promise((r) => setTimeout(r, 250));
        await supabase.auth.getSession();
        await new Promise((r) => setTimeout(r, 400));
        ({ error } = await runUpdate());
      }

      if (error) {
        console.error('重置密碼錯誤:', error);
        return { success: false, error: translateErrorMessage(error.message) };
      }

      console.log('密碼重置成功');
      return { success: true, error: null };
    } catch (error) {
      console.error('重置密碼錯誤:', error);
      return { success: false, error: '密碼重置失敗，請稍後再試' };
    }
  }

  /**
   * 根據 email 查找用戶
   */
  static async getUserByEmail(email: string): Promise<User | null> {
    try {
      console.log('根據 email 查找用戶:', email);

      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('email', email)
        .single();

      if (error) {
        console.error('根據 email 查找用戶失敗:', error);
        throw new Error(`查找用戶失敗: ${error.message}`);
      }

      if (!data) {
        console.log('未找到用戶:', email);
        return null;
      }

      const user: User = {
        id: data.id,
        email: data.email,
        name: data.name,
        phone: data.phone,
        userType: data.user_type,
        profileImage: data.profile_image || '',
        addresses: data.addresses || [],
        isVerified: !!data.is_verified,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };

      console.log('找到用戶:', user);
      return user;
    } catch (error) {
      console.error('UserService.getUserByEmail 錯誤:', error);
      throw error;
    }
  }

  /**
   * 根據姓名查找用戶（模糊匹配）
   */
  static async getUserByName(name: string): Promise<User | null> {
    try {
      console.log('根據姓名查找用戶:', name);

      const { data, error } = await supabase
        .from('users')
        .select('*')
        .ilike('name', `%${name}%`)
        .limit(1)
        .single();

      if (error) {
        console.error('根據姓名查找用戶失敗:', error);
        throw new Error(`查找用戶失敗: ${error.message}`);
      }

      if (!data) {
        console.log('未找到用戶:', name);
        return null;
      }

      const user: User = {
        id: data.id,
        email: data.email,
        name: data.name,
        phone: data.phone,
        userType: data.user_type,
        profileImage: data.profile_image || '',
        addresses: data.addresses || [],
        isVerified: !!data.is_verified,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };

      console.log('找到用戶:', user);
      return user;
    } catch (error) {
      console.error('UserService.getUserByName 錯誤:', error);
      throw error;
    }
  }

  /**
   * 根據用戶 ID 獲取用戶信息
   */
  static async getUserById(userId: string): Promise<User | null> {
    try {
      console.log('根據 ID 查找用戶:', userId);

      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        console.error('根據 ID 查找用戶失敗:', error);
        throw new Error(`查找用戶失敗: ${error.message}`);
      }

      if (!data) {
        console.log('未找到用戶:', userId);
        return null;
      }

      const user: User = {
        id: data.id,
        email: data.email,
        name: data.name,
        phone: data.phone,
        userType: data.user_type,
        profileImage: data.profile_image || '',
        addresses: data.addresses || [],
        isVerified: !!data.is_verified,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };

      console.log('找到用戶:', user);
      return user;
    } catch (error) {
      console.error('UserService.getUserById 錯誤:', error);
      throw error;
    }
  }
}

// 注册服务类 - 移到文件末尾
class CustomerRegistrationService {
  async register(data: any): Promise<User> {
    // 客户注册逻辑
    return {} as User;
  }
}

class CraftsmanRegistrationService {
  async register(data: any): Promise<User> {
    // 工匠注册逻辑
    return {} as User;
  }
}

class CompanyRegistrationService {
  async register(data: any): Promise<User> {
    // 公司注册逻辑
    return {} as User;
  }
}

class AdminRegistrationService {
  async register(data: any): Promise<User> {
    // 管理员注册逻辑
    return {} as User;
  }
}


